import { useCallback, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, Menu, X } from "lucide-react";
import { Link } from "react-router-dom";
import { Logo } from "./Logo";
import { MobileMenu } from "./MobileMenu";
import { useNavLinks } from "./useCases";
import { BOOK_PERIO_PATH, FINANCING_PATH } from "./data";
import { scrollToId, EASE } from "./Reveal";

export const Nav = () => {
  const links = useNavLinks();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const close = useCallback(() => setOpen(false), []);
  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 40);
    on();
    window.addEventListener("scroll", on, { passive: true });
    return () => window.removeEventListener("scroll", on);
  }, []);

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 1, delay: 0.1, ease: EASE }}
      className="fixed top-0 inset-x-0 z-50 px-4 sm:px-8 pt-4"
      data-testid="site-nav"
    >
      <div
        className={`mx-auto max-w-[1400px] flex items-center justify-between rounded-full px-4 sm:px-6 py-3 transition-[background-color,box-shadow,border-color] duration-500 border ${
          scrolled || open ? "bg-[#FCFBF7]/85 backdrop-blur-xl border-[#1B3B28]/10 shadow-[0_10px_40px_rgba(27,59,40,0.08)]" : "bg-transparent border-transparent"
        }`}
      >
        <button onClick={() => window.__lenis?.scrollTo(0)} data-testid="nav-logo-btn" aria-label="Torna in cima">
          <Logo />
        </button>
        <nav className="hidden xl:flex items-center">
          {links.map((l) => (
            <button
              key={l.id}
              onClick={() => scrollToId(l.id)}
              data-testid={`nav-link-${l.id}`}
              className={`group relative whitespace-nowrap px-2.5 2xl:px-4 py-2 text-sm text-[#1E2922]/80 hover:text-[#1B3B28] transition-colors ${["team", "faq", "dottore"].includes(l.id) ? "hidden 2xl:inline-block" : ""}`}
            >
              {l.label}
              <span className="absolute left-4 right-4 bottom-1 h-px bg-[#3E6B48] origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-500" />
            </button>
          ))}
          <Link to="/blog" data-testid="nav-link-blog" className="group relative whitespace-nowrap px-2.5 2xl:px-4 py-2 text-sm text-[#1E2922]/80 hover:text-[#1B3B28] transition-colors">
            Blog
            <span className="absolute left-4 right-4 bottom-1 h-px bg-[#3E6B48] origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-500" />
          </Link>
          <Link to="/igiene-gbt" data-testid="nav-link-gbt" className="group relative whitespace-nowrap px-2.5 2xl:px-4 py-2 text-sm text-[#1E2922]/80 hover:text-[#1B3B28] transition-colors">
            Igiene GBT
            <span className="absolute left-4 right-4 bottom-1 h-px bg-[#3E6B48] origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-500" />
          </Link>
          <Link to={FINANCING_PATH} data-testid="nav-link-financing" className="group relative whitespace-nowrap px-2.5 2xl:px-4 py-2 text-sm text-[#1E2922]/80 hover:text-[#1B3B28] transition-colors">
            Rate 0%
            <span className="absolute left-4 right-4 bottom-1 h-px bg-[#3E6B48] origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-500" />
          </Link>
        </nav>
        <div className="flex items-center gap-2">
          <Link
            to={BOOK_PERIO_PATH}
            data-testid="nav-cta-booking"
            className="group hidden sm:flex shrink-0 whitespace-nowrap items-center gap-2 rounded-full bg-[#1B3B28] text-[#FCFBF7] pl-5 pr-2 py-2 text-sm hover:bg-[#3E6B48] transition-colors"
          >
            Prima visita
            <span className="grid place-items-center w-7 h-7 rounded-full bg-[#86C89B] text-[#0F2317] transition-transform duration-500 group-hover:rotate-45">
              <ArrowUpRight size={15} />
            </span>
          </Link>
          <button
            onClick={() => setOpen((o) => !o)}
            aria-label={open ? "Chiudi menu" : "Apri menu"}
            aria-expanded={open}
            data-testid="mobile-menu-toggle"
            className="2xl:hidden shrink-0 flex items-center gap-2 rounded-full bg-[#E6ECE4] text-[#1B3B28] pl-4 pr-3 py-2.5 text-sm hover:bg-[#D5DFD3] transition-colors"
          >
            {open ? "Chiudi" : "Menu"}
            <motion.span animate={{ rotate: open ? 90 : 0 }} transition={{ duration: 0.3 }}>{open ? <X size={16} /> : <Menu size={16} />}</motion.span>
          </button>
        </div>
      </div>
      <MobileMenu open={open} onClose={close} />
    </motion.header>
  );
};
